import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Form, { SubmitType } from '../../../../../components/form';
import { useTranslation } from 'react-i18next';
import css from '../view.module.css';
import Group from '../../../../../components/form/elements/group';
import Text from '../../../../../components/form/elements/text';
import Disabled from '../../../../../components/form/elements/disabled';
import Link from '../../../../../components/form/elements/link';
import { can, route } from '../../../../../services/utils';
import { LIVESTOCK_ANIMALS_AND_EVENTS_INDEX } from '../../../../../router/routes';
import Button from '../../../../../components/form/elements/button';
import { useParams } from 'react-router-dom';
import AnimalsAndEventsTabKeys from '../../enums/animals-and-events-tab-keys';
import RadioGroup from '../../../../../components/form/elements/radio-group';
import { useService } from '../../../../../services/services';
import './style.css';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
enum AnimalType {
  dairy = 'dairy',
  meat = 'meat',
}
export default function AnimalInformation({ animal }) {
  const { farmId, id } = useParams<{
    farmId: string;
    id: string;
  }>();
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.livestock.animals_and_events.animals';
  const { animalService } = useService();
  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));
  useEffect(() => {
    if (animal) {
      setBodyElements([
        Group(
          [
            Group([
              Disabled(
                Text('animal_id', t(`${transKeyPrefix}.view.animal_id`))
              ),
              Text('eid', t(`${transKeyPrefix}.view.animal_eid`)),
              Disabled(Text('name', t(`${transKeyPrefix}.name`))),
              Disabled(
                Text('categories_concatenated', t(`${transKeyPrefix}.group`))
              ),
              Disabled(Text('birth_date', t(`${transKeyPrefix}.birth_date`))),
              Disabled(Text('breed_code', t(`${transKeyPrefix}.breed`))),
              Disabled(Text('status', t(`${transKeyPrefix}.status`))),
              Disabled(Text('lactation', t(`${transKeyPrefix}.lactation`))),
            ]),
            Disabled(
              Group([
                Text('herd_id', t(`${transKeyPrefix}.herd_nr`)),
                Text('barn_ldc_nr', t(`${transKeyPrefix}.barn_nr`)),
                Text('species_group', t(`${transKeyPrefix}.species`)),
                Text('gender', t(`${transKeyPrefix}.gender`)),
                Text('age', t(`${transKeyPrefix}.age`)),
                Text('blood', t(`${transKeyPrefix}.blood`)),
                Text('hair_color', t(`${transKeyPrefix}.hair_color`)),
                RadioGroup(
                  'type',
                  [
                    {
                      value: AnimalType.dairy,
                      label: t(`${transKeyPrefix}.animal_type.dairy`),
                    },
                    {
                      value: AnimalType.meat,
                      label: t(`${transKeyPrefix}.animal_type.meat`),
                    },
                  ],
                  `${transKeyPrefix}.tip`
                ),
              ])
            ),
          ],
          '',
          true
        ),
        Disabled(
          Group(
            [
              Group([
                () => {
                  return (
                    <>
                      <legend className={'text-right !pr-8 !pb-5'}>
                        {t(`${transKeyPrefix}.mother`)}
                      </legend>
                    </>
                  );
                },
                Text('mother.animal_id', t(`${transKeyPrefix}.view.animal_id`)),
                Text('mother.name', t(`${transKeyPrefix}.name`)),
                Text('mother.breed_code', t(`${transKeyPrefix}.breed`)),
                Text('mother.CG_nr', t(`${transKeyPrefix}.CG_nr`)),
              ]),
              Group([
                () => {
                  return (
                    <>
                      <legend className={'text-right !pr-8 !pb-5'}>
                        {t(`${transKeyPrefix}.father`)}
                      </legend>
                    </>
                  );
                },
                Text('father.animal_id', t(`${transKeyPrefix}.view.animal_id`)),
                Text('father.name', t(`${transKeyPrefix}.name`)),
                Text('father.breed_code', t(`${transKeyPrefix}.breed`)),
                Text('father.CG_nr', t(`${transKeyPrefix}.CG_nr`)),
              ]),
            ],
            '',
            true
          )
        ),
      ]);
      setFooterElements(
        [
          can(farmUser, { title: 'update animal' })
            ? Button('save', null, t('save'))
            : null,
          Link(
            route(LIVESTOCK_ANIMALS_AND_EVENTS_INDEX, {
              farmId: farmId,
              tab: AnimalsAndEventsTabKeys.animals,
            }),
            'cancel'
          ),
        ].filter(Boolean)
      );
    }
  }, [animal, t, transKeyPrefix, farmId, farmUser]);

  const getFormattedDate = (date?: string) => {
    return (
      date &&
      new Date(date).toLocaleDateString('lv-Lv', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      })
    );
  };
  const getFormatedAge = (value?: string) => {
    if (value) {
      if (value === '0') {
        return value;
      } else {
        const dateValues = value.split(' ');
        const dateElement = ['g. ', 'mēn. ', ' d.'];
        let date = '';
        for (let index = 0; index < dateElement.length; index++) {
          if (dateValues[index] !== '0') {
            date += dateValues[index] + dateElement[index];
          }
        }
        return date;
      }
    }
  };
  const animalData = useMemo(
    () => ({
      ...animal,
      birth_date: getFormattedDate(animal?.birth_date),
      age: getFormatedAge(animal?.age),
      mother: animal?.mother
        ? {
            ...animal?.mother,
            age: getFormatedAge(animal?.mother?.age),
          }
        : null,
      father: animal?.father
        ? {
            ...animal?.father,
            age: getFormatedAge(animal?.father?.age),
          }
        : null,
    }),
    [animal]
  );

  const submit = useCallback<SubmitType<any>>(
    (data) => {
      return [animalService.update(farmId, id, { eid: data.eid }), () => {}];
    },
    [animalService, farmId, id]
  );

  return (
    <Form
      title={''}
      mode="edit"
      noticeAccessor="ViewForm"
      body={bodyElements}
      footer={footerElements}
      values={animalData}
      className={css['field-form']}
      hideHeader={true}
      submit={submit}
    />
  );
}
